import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import {
    Box,
    Typography,
    Grid,
    Pagination,
    CircularProgress,
    Fade,
    Container,
    Paper,
    InputBase,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    RocketLaunchRounded,
    ArrowBackRounded,
    SearchRounded,
    CloseRounded,
    FilterListRounded,
    CategoryRounded,
} from "@mui/icons-material";
import { Footer } from "../../components/Layout/Footer";
import { Header1 } from "../../components/Layout/Header1.js";
import CardProduct from "../../components/Products/CardProduct";
import MethodGet from "../../config/Service";
import FilterPanel from "../Catalogo/FilterPanel";
import SEO from "../../components/SEO/SEO";

const LaunchesPage = () => {
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Data states
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [brands, setBrands] = useState([]);

    // Filter states
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [priceRange, setPriceRange] = useState([0, 10000]);
    const [debouncedPriceRange, setDebouncedPriceRange] = useState([0, 10000]);
    const [isPriceRangeManuallySet, setIsPriceRangeManuallySet] = useState(false);
    const [sortBy, setSortBy] = useState("default");
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);

    // Products
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Search state
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [page, setPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(24);

    // Debounce helpers
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedPriceRange(priceRange), 500);
        return () => clearTimeout(timer);
    }, [priceRange]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 600);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Load Initial Data
    useEffect(() => {
        // Categories
        MethodGet("/cate")
            .then((res) => {
                const cats = res.data.categories || [];
                setCategories(cats);
                // Flatten subcategories for filter (optional, or depend on category selection)
                const allSubs = cats.reduce((acc, c) => [...acc, ...(c.subcategories || [])], []);
                setSubcategories(allSubs);
            })
            .catch((err) => console.error(err));

        // Brands
        MethodGet("/distribucion/products/brands")
            .then((res) => setBrands(res.data || []))
            .catch((err) => console.error(err));
    }, []);

    // Load Products
    useEffect(() => {
        setLoading(true);

        const params = new URLSearchParams();
        params.set("page", page);
        params.set("per_page", itemsPerPage);

        // Use a filter to identify launches if we use "all" endpoint, 
        // OR simply pass filters to "launches" endpoint if supported.
        // Assuming "launches" endpoint supports standard filters.

        if (selectedCategory) params.set("category_id", selectedCategory);
        if (selectedSubcategory) params.set("subcategory_id", selectedSubcategory);
        if (selectedBrand) params.set("brand_id", selectedBrand);
        if (sortBy !== "default") params.set("sort_by", sortBy);
        if (debouncedSearch) params.set("search", debouncedSearch);
        if (isPriceRangeManuallySet) {
            params.set("min_price", debouncedPriceRange[0]);
            params.set("max_price", debouncedPriceRange[1]);
        }

        // Using launches endpoint!
        MethodGet(`/distribucion/products/launches?${params.toString()}`)
            .then((res) => {
                setProducts(res.data.data || res.data || []);
                setTotalProducts(
                    res.data.total || (res.data.data ? res.data.data.length : 0)
                );
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error loading launches:", err);
                setLoading(false);
            });
    }, [
        page,
        itemsPerPage,
        sortBy,
        debouncedSearch,
        selectedCategory,
        selectedSubcategory,
        selectedBrand,
        debouncedPriceRange
    ]);

    const handleClearFilters = () => {
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSelectedBrand(null);
        setIsPriceRangeManuallySet(false);
        setPriceRange([0, 10000]);
        setSortBy("default");
        setSearchTerm("");
        setPage(1);
    };

    return (
        <Box sx={{ backgroundColor: "#fcfcfc", minHeight: "100vh" }}>
            <SEO
                title="Lanzamientos - MerryColor"
                description="Descubre los nuevos lanzamientos y productos exclusivos de MerryColor."
                url="/lanzamientos"
            />

            <Header1 />

            {/* Hero Section */}
            <Box
                sx={{
                    background: "linear-gradient(135deg, #4A0033 0%, #801F56 40%, #e94560 100%)",
                    pt: { xs: 4, md: 6 },
                    pb: { xs: 6, md: 8 },
                    px: 2,
                    position: "relative",
                    overflow: "hidden",
                    color: "white"
                }}
            >
                {/* Abstract Shapes */}
                <Box sx={{
                    position: "absolute",
                    top: -100,
                    left: -100,
                    width: 500,
                    height: 500,
                    background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                    filter: "blur(50px)",
                }} />

                <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
                    <Link
                        to="/catalogo"
                        style={{
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            color: "rgba(255,255,255,0.8)",
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            marginBottom: 24,
                        }}
                    >
                        <ArrowBackRounded sx={{ fontSize: 18 }} />
                        Volver al catálogo
                    </Link>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <Box
                            sx={{
                                width: { xs: 80, md: 100 },
                                height: { xs: 80, md: 100 },
                                borderRadius: "50%",
                                background: "rgba(255,255,255,0.15)",
                                backdropFilter: "blur(10px)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                                color: "#fff",
                                border: "1px solid rgba(255,255,255,0.2)"
                            }}
                        >
                            <RocketLaunchRounded sx={{ fontSize: { xs: 40, md: 50 } }} />
                        </Box>

                        <Box>
                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: "2rem", md: "3.5rem" },
                                    color: "#fff",
                                    lineHeight: 1.1,
                                    mb: 0.5,
                                    textShadow: "0 4px 10px rgba(0,0,0,0.3)"
                                }}
                            >
                                Lanzamientos
                            </Typography>
                            <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "1.1rem", fontWeight: 500 }}>
                                Lo más nuevo y exclusivo ha llegado
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Main Layout */}
            <Container maxWidth="xl" sx={{ mt: -4, position: "relative", zIndex: 10, pb: 8 }}>
                <Grid container spacing={4}>
                    {/* Sidebar Filters */}
                    <Grid item xs={12} md={3}>
                        <Box sx={{ position: "sticky", top: 120 }}>
                            <FilterPanel
                                categories={categories}
                                selectedCategory={selectedCategory}
                                onCategoryChange={(val) => { setSelectedCategory(val); setPage(1); }}

                                subcategories={subcategories}
                                selectedSubcategory={selectedSubcategory}
                                onSubcategoryChange={(val) => { setSelectedSubcategory(val); setPage(1); }}

                                brands={brands}
                                selectedBrand={selectedBrand}
                                onBrandChange={(val) => { setSelectedBrand(val); setPage(1); }}

                                minPrice={0}
                                maxPrice={10000}
                                priceRange={priceRange}
                                onPriceChange={(val, manual) => { setPriceRange(val); if (manual) setIsPriceRangeManuallySet(true); }}

                                sortBy={sortBy}
                                onSortChange={setSortBy}

                                onClearFilters={handleClearFilters}

                                isMobile={isMobile}
                                isOpen={filterPanelOpen}
                                onToggle={() => setFilterPanelOpen(!filterPanelOpen)}
                            />
                        </Box>
                    </Grid>

                    {/* Products Column */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ mb: 4, display: "flex", gap: 2 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: "#fff",
                                    borderRadius: "12px",
                                    px: 2,
                                    py: 1,
                                    border: "1px solid #eee",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                                    transition: "all 0.2s",
                                    "&:focus-within": {
                                        borderColor: "#e94560",
                                        boxShadow: "0 4px 12px rgba(233,69,96,0.1)",
                                    }
                                }}
                            >
                                <SearchRounded sx={{ color: "#aaa", mr: 1.5 }} />
                                <InputBase
                                    placeholder="Buscar en lanzamientos..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{ flex: 1, fontSize: "1rem" }}
                                />
                                {searchTerm && (
                                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                                        <CloseRounded fontSize="small" />
                                    </IconButton>
                                )}
                            </Paper>

                            {isMobile && (
                                <IconButton
                                    onClick={() => setFilterPanelOpen(true)}
                                    sx={{
                                        backgroundColor: "#111",
                                        color: "#fff",
                                        borderRadius: "12px",
                                        width: 56,
                                        '&:hover': { backgroundColor: "#333" }
                                    }}
                                >
                                    <FilterListRounded />
                                </IconButton>
                            )}
                        </Box>

                        {loading ? (
                            <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
                                <CircularProgress sx={{ color: "#e94560" }} />
                            </Box>
                        ) : products.length > 0 ? (
                            <Fade in={true}>
                                <Box>
                                    <Grid container spacing={3}>
                                        {products.map((product) => (
                                            <Grid item xs={6} md={4} lg={4} key={product.id}>
                                                <CardProduct product={product} />
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {totalProducts > itemsPerPage && (
                                        <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
                                            <Pagination
                                                count={Math.ceil(totalProducts / itemsPerPage)}
                                                page={page}
                                                onChange={(e, v) => { setPage(v); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                                                color="secondary"
                                                size="large"
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Fade>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                                <RocketLaunchRounded sx={{ fontSize: 60, color: '#eee' }} />
                                <Typography variant="h6" color="textSecondary">No se encontraron lanzamientos.</Typography>
                                <Typography variant="body2" color="textSecondary">Intenta ajustar los filtros.</Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Container>

            <Footer />
        </Box>
    );
};

export default LaunchesPage;
